import React, { useState, useCallback, useRef } from 'react';
import { useFullscreenView } from '../../Hooks/useFullscreenView';
import TouchFullscreenInput from '../../Components/TouchFullscreen/TouchFullscreenInput';

interface IInputGroupContext {
  activeId: number | null;
  ref: HTMLDivElement | null;
  optionsRef: HTMLDivElement | null;
  navigateNext: () => void;
  inputGroupFocus?: (id: number) => void;
  registerContent?: () => number;
  unregisterContent: (id: number) => void;
}

interface IProps {
  usesOldAutocomplete?: boolean;
}

export const EDInputGroupContext = React.createContext<IInputGroupContext>({
  activeId: null,
  ref: null,
  optionsRef: null,
  navigateNext: () => {},
  unregisterContent: (_id: number) => {},
});

/*
 * Wrapping an InputGroup around a group of nodes that include EDAutocompleteEdit, EDDatePicker or EDTextEdit components will enable will
 * add a FullscreenTouch view on mobile where the user can step between the different inputs.
 */
export const EDInputGroup: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [optionsRef, setOptionsRef] = useState<HTMLDivElement | null>(null);
  const fullscreenView = useFullscreenView();

  const [registrationOrder, setRegistrationOrder] = useState<number[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const nextIdRef = useRef(0);

  // Ref mirrors — provide stable references to latest values for use inside callbacks
  const registrationOrderRef = useRef<number[]>([]);
  const activeIdRef = useRef<number | null>(null);

  const setRegistrationOrderAndRef = useCallback(
    (updater: React.SetStateAction<number[]>) => {
      setRegistrationOrder(prev => {
        const next =
          typeof updater === 'function'
            ? (updater as (prevState: number[]) => number[])(prev)
            : updater;
        registrationOrderRef.current = next;
        return next;
      });
    },
    []
  );

  const setActiveIdAndRef = useCallback(
    (updater: React.SetStateAction<number | null>) => {
      setActiveId(prev => {
        const next =
          typeof updater === 'function'
            ? (updater as (prevState: number | null) => number | null)(prev)
            : updater;
        activeIdRef.current = next;
        return next;
      });
    },
    []
  );

  // Derived — no extra state needed
  const maxContentIndex = registrationOrder.length;
  const activePos = activeId === null ? -1 : registrationOrder.indexOf(activeId);
  const contentIndex = activePos === -1 ? null : activePos + 1;

  const registerContent = useCallback((): number => {
    const id = ++nextIdRef.current;
    setRegistrationOrderAndRef(prev => [...prev, id]);
    return id;
  }, [setRegistrationOrderAndRef]);

  const unregisterContent = useCallback((id: number) => {
    const isRegistered = registrationOrderRef.current.includes(id);
    if (!isRegistered) {
      return;
    }

    setActiveIdAndRef(prev => (prev === id ? null : prev));
    setRegistrationOrderAndRef(prev =>
      prev.filter(existingId => existingId !== id)
    );
  }, [setActiveIdAndRef, setRegistrationOrderAndRef]);

  const close = useCallback(
    (event: any) => {
      event.stopPropagation();
      event.preventDefault();
      setActiveIdAndRef(null);
    },
    [setActiveIdAndRef]
  );

  // Stable callbacks — read from refs at call time to avoid stale closure issues.
  const inputGroupFocus = useCallback((id: number) => {
    const order = registrationOrderRef.current;
    setActiveIdAndRef(order.includes(id) ? id : null);
  }, [setActiveIdAndRef]);

  const navigatePrev = useCallback(() => {
    const currentActiveId = activeIdRef.current;
    const order = registrationOrderRef.current;
    if (currentActiveId !== null) {
      const currentPos = order.indexOf(currentActiveId);
      if (currentPos > 0) {
        setActiveIdAndRef(order[currentPos - 1] ?? null);
      }
    }
  }, [setActiveIdAndRef]);

  const navigateNext = useCallback(() => {
    const currentActiveId = activeIdRef.current;
    const order = registrationOrderRef.current;
    if (currentActiveId !== null) {
      const currentPos = order.indexOf(currentActiveId);
      if (currentPos === order.length - 1) {
        setActiveIdAndRef(null);
      } else {
        setActiveIdAndRef(order[currentPos + 1] ?? null);
      }
    }
  }, [setActiveIdAndRef]);

  return (
    <EDInputGroupContext.Provider
      value={{
        activeId: activeId,
        inputGroupFocus: inputGroupFocus,
        registerContent: registerContent,
        unregisterContent: unregisterContent,
        navigateNext: navigateNext,
        ref: ref,
        optionsRef: optionsRef,
      }}
    >
      <TouchFullscreenInput
        open={fullscreenView && contentIndex !== null}
        close={close}
        onCancel={close}
        navigatePrev={navigatePrev}
        navigateNext={navigateNext}
        currentView={contentIndex !== null ? contentIndex : 1}
        totalViews={maxContentIndex}
        setRef={setRef}
        setOptionsRef={setOptionsRef}
        usesOldAutocomplete={props.usesOldAutocomplete}
      />
      {props.children}
    </EDInputGroupContext.Provider>
  );
};

export const EDInputGroupContextProvider = EDInputGroupContext.Provider;
export const EDInputGroupContextConsumer = EDInputGroupContext.Consumer;
